import { useState } from "react";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, FileText, Download, Send } from "lucide-react";

export const OwnerLetter = ({ analysis, buildPayload }) => {
  const [letter, setLetter] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [recipient, setRecipient] = useState("ElezaF@JanteLuxuryTravel.com");

  const isGo = analysis?.overall === "GO";

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await adminApi.post("/admin/property-scout/letter", {
        ...buildPayload(),
        analysis,
      });
      setLetter(r.data);
      toast.success(
        r.data.hubspot_note_id
          ? "Owner letter drafted — and logged to the property's HubSpot deal."
          : "Owner letter drafted by Sasha."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Letter generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const download = async () => {
    try {
      const r = await adminApi.get(
        `/admin/property-scout/letter/${letter.id}/pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "jante-owner-letter-package.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("Could not download the package.");
    }
  };

  const email = async () => {
    if (!recipient) return;
    setEmailing(true);
    try {
      await adminApi.post(`/admin/property-scout/letter/${letter.id}/email`, {
        recipient_email: recipient,
      });
      toast.success(`Letter package emailed to ${recipient}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email failed.");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="border border-[#E3DED5] p-4" data-testid="owner-letter-block">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="eyebrow">Owner letter</div>
        <button
          type="button"
          onClick={generate}
          disabled={!isGo || generating}
          data-testid="letter-generate-btn"
          title={isGo ? "Sasha drafts a letter to the property owner" : "Lights up when the verdict is GO"}
          className={
            isGo
              ? "btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em]"
              : "inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border border-[#E3DED5] text-[#8E8F90] cursor-not-allowed"
          }
        >
          {generating ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
          {generating ? "Sasha is writing…" : "Generate letter"}
        </button>
      </div>
      {!isGo && !letter && (
        <p className="mt-2 text-xs text-[#6E6E70]">
          Activates on a <span className="font-semibold text-[#2D4C3E]">GO</span> verdict — Sasha will draft a
          letter to the property address with the Thrive deck and a property-specific income statement.
        </p>
      )}
      {letter && (
        <div className="mt-4 space-y-4" data-testid="letter-result">
          <div className="bg-[#FAF7F2] border border-[#E3DED5] p-4 max-h-64 overflow-y-auto text-sm text-[#2A1F1A] whitespace-pre-wrap font-serif-display">
            {letter.letter_text}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <button
              type="button"
              onClick={download}
              data-testid="letter-download-btn"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em]"
            >
              <Download size={13} /> Download package (PDF)
            </button>
            <div className="flex gap-2 flex-1">
              <Input
                data-testid="letter-email-input"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                type="email"
                placeholder="owner@example.com"
                className="editorial-input h-9 text-sm"
              />
              <button
                type="button"
                onClick={email}
                disabled={emailing}
                data-testid="letter-email-btn"
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] whitespace-nowrap"
              >
                {emailing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Email
              </button>
            </div>
          </div>
          <p className="text-[11px] text-[#8E8F90]">
            Package = branded letter + income statement + Janté Thrive deck, one PDF. Print &amp; mail to the
            property address, or email it.
          </p>
        </div>
      )}
    </div>
  );
};
